import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import {
  FormGroup, Button, Form, Collapse, Label,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Formik } from 'formik';
import * as Yup from 'yup';

import useSelect from '../hooks/useSelect';
import userService from '../services/userService';
import FormModal from '../components/FormModal';
import RequiredField from '../components/RequiredField';
import Required from '../components/Required';

export default function AddGuardianFamilyForm(props) {
  const {
    addGuardians, canCreate, addCreatedGuardian,
  } = props;

  const minSearchChars = 3;

  const loadGuardiansForSelect = async (searchTerm = undefined) => {
    if (searchTerm.length < minSearchChars) {
      return [];
    }

    const guardians = await userService.getGuardians(searchTerm);
    const options = guardians.model.map((g) => ({
      value: g.id,
      label: `${g.firstName} ${g.lastName}`,
      guardian: g,
    }));
    return options;
  };

  const { value: selectedGuardians, setValue: setGuardians, bind: bindGuardians } = useSelect();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    firstName: '',
    lastName: '',
    emailAddress: '',
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .max(100, 'First Name must not exceed 100 characters.')
      .required('The First Name field is required.'),

    lastName: Yup.string()
      .max(100, 'Last Name must not exceed 100 characters.')
      .required('The Last Name field is required.'),

    emailAddress: Yup.string()
      .email('Please enter a valid Email Address.')
      .required('The Email Address field is required.'),
  });

  const onFormSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);

    const obj = {
      username: values.emailAddress,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.emailAddress,
      role: 'Guardian',
    };

    await userService
      .addUser(obj)
      .then(async (r) => {
        if (r.hasError) {
          toast.error(r.errorMessage);
        } else {
          addCreatedGuardian(r.model);
          resetForm(initialValues);
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const ids = selectedGuardians.map((g) => g.guardian);
    addGuardians(ids);

    // Clear the dropdown.
    setGuardians([]);
  };

  return (
    <div>
      <a
        className="mb-3 d-block d-flex align-items-center collapsed addRow"
        href="#guardian-form"
        data-toggle="collapse"
        aria-expanded="false"
        aria-controls="child-form"
      >
        <span className="circle-dashed"><FontAwesomeIcon icon="plus" /></span>
        <h5 className="ml-3 mb-0 text-primary">Add Parent/Guardian</h5>
      </a>
      <Collapse id="guardian-form">
        <Form onSubmit={handleSubmit}>
          <FormGroup className="mb-1">
            <div className="d-flex justify-content-between align-items-center">
              <Label className="mb-0">
                Choose Parent/Guardian&nbsp;
                <Required />
              </Label>
              {canCreate && (
                <FormModal
                  className="btn btn-link btn-sm pr-0"
                  buttonLabel="Add New"
                  isSubmitting={isSubmitting}
                  formName="userDetailForm"
                >
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={onFormSubmit}
                  >
                    {(p) => (
                      <Form id="userDetailForm" onSubmit={p.handleSubmit}>
                        <RequiredField formKey="firstName" labelName="First Name" inputPlaceholder="First Name" />
                        <RequiredField formKey="lastName" labelName="Last Name" inputPlaceholder="Last Name" />
                        <RequiredField formKey="emailAddress" labelName="Email Address" inputPlaceholder="Email Address" />
                      </Form>
                    )}
                  </Formik>
                </FormModal>
              )}
            </div>
            <AsyncSelect loadOptions={loadGuardiansForSelect} placeholder={`Please type at least ${minSearchChars} characters to search`} {...bindGuardians} className="w-100" size="sm" closeMenuOnSelect={false} isMulti />
          </FormGroup>
          <Button className="btn-falcon-primary mr-2" size="sm" type="submit">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            Add to Family
          </Button>
        </Form>
        <hr className="border-dashed border-bottom-0" />
      </Collapse>
    </div>
  );
}
